// Signature logo, placeholder block, work media renderer, lightbox, dropzone, toast.

import React from "react";
import { imageUrl, PAPER_PALETTES } from "./api.js";

export function Signature({ size = 30, onClick, className = "" }) {
  return (
    <span
      className={`signature-logo ${className}`}
      style={{ fontSize: size }}
      onClick={onClick}
      role="link"
      aria-label="Chris Dicker — home"
    >
      Chris Dicker
    </span>
  );
}

export function Placeholder({ seed = 0, aspect = "4/3", label = "painting", showLabel = true, style = {} }) {
  const palette = PAPER_PALETTES[seed % PAPER_PALETTES.length];
  const [c1, c2] = palette;
  const ang = (seed * 37) % 180;
  return (
    <div
      className="ph"
      style={{
        aspectRatio: aspect,
        background: `linear-gradient(${ang}deg, ${c1} 0%, ${c1} 49%, ${c2} 49%, ${c2} 51%, ${c1} 51%, ${c1} 100%)`,
        ...style,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0,
          background: `repeating-linear-gradient(${ang + 90}deg, transparent 0 36px, rgba(255,255,255,.05) 36px 38px)`,
        }}
      />
      {showLabel && <div className="ph-label">{label}</div>}
    </div>
  );
}

export function WorkMedia({ work, fit = "contain", showLabel = true }) {
  if (!work?.image) return <Placeholder seed={0} aspect="4/3" label="no image" />;
  if (work.image.kind === "upload") {
    return <img src={imageUrl(work.image.key)} alt={work.title || ""} style={{ width: "100%", height: "100%", objectFit: fit }} />;
  }
  return (
    <Placeholder
      seed={work.image.seed}
      aspect={work.image.aspect}
      label={showLabel ? (work.image.label || "painting") : ""}
      showLabel={showLabel}
    />
  );
}

export function Lightbox({ works, index, onClose, onIndex }) {
  React.useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onIndex((index - 1 + works.length) % works.length);
      else if (e.key === "ArrowRight") onIndex((index + 1) % works.length);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [index, works.length, onIndex, onClose]);

  const w = works[index];
  if (!w) return null;
  return (
    <div className="lb" onClick={(e) => {
      if (e.target.classList.contains("lb") || e.target.classList.contains("lb-body")) onClose();
    }}>
      <div className="lb-head">
        <span>{w.title}</span>
        <span>{String(index + 1).padStart(2, "0")} / {String(works.length).padStart(2, "0")}</span>
        <button className="lb-x" onClick={onClose} aria-label="close">×</button>
      </div>
      <div className="lb-body">
        <button className="lb-arrow prev" onClick={() => onIndex((index - 1 + works.length) % works.length)} aria-label="previous">←</button>
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {w.image?.kind === "upload"
            ? <img className="lb-img" src={imageUrl(w.image.key)} alt={w.title} />
            : <div style={{ width: "min(75vw, 1100px)", height: "min(78vh, 800px)", display: "flex" }}>
                <Placeholder seed={w.image?.seed || 0} aspect={w.image?.aspect || "4/3"} label={w.image?.label || "painting"} />
              </div>}
        </div>
        <button className="lb-arrow next" onClick={() => onIndex((index + 1) % works.length)} aria-label="next">→</button>
      </div>
      <div className="lb-cap">
        <div className="lc-l">
          <span className="lc-title">{w.title}</span>
          {w.year && <span className="lc-meta">{w.year}</span>}
          {w.medium && <span className="lc-meta">{w.medium}</span>}
          {w.dimensions && <span className="lc-meta">{w.dimensions}</span>}
        </div>
        {w.caption && <div className="lc-meta" style={{ maxWidth: "40ch", textAlign: "right" }}>{w.caption}</div>}
      </div>
    </div>
  );
}

export function useToast() {
  const [msg, setMsg] = React.useState(null);
  React.useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(null), 2400);
    return () => clearTimeout(t);
  }, [msg]);
  const node = msg ? <div className="toast">{msg}</div> : null;
  return [node, setMsg];
}

export function DropZone({ onFiles, big = false, label = "Drop images or click to upload" }) {
  const [over, setOver] = React.useState(false);
  const inputRef = React.useRef(null);
  const handle = (files) => {
    const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (arr.length) onFiles(arr);
  };
  return (
    <div
      className={`dropzone${big ? " big" : ""}${over ? " over" : ""}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); handle(e.dataTransfer.files); }}
    >
      <span>{label}</span>
      <input
        ref={inputRef}
        type="file" accept="image/*" multiple
        style={{ display: "none" }}
        onChange={(e) => { handle(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}
